// Implements: F-SHR-006, F-SHR-008, F-UIX-007
/**
 * The form that changes one link's expiry and password (the label is renamed in its row, mockup
 * v3 P8; the mode and the artifact are fixed at creation). Pure presentation: the draft and the
 * answer come as props.
 *
 * The expiry is "Sin cambios" or one of the four v3 choices; a link that never expires opens on
 * "1 año", with the help that says what it means. A blank password leaves it as it is. The password field states no
 * minimum of its own: the minimum is the server's setting
 * (`SHARE_PASSWORD_MIN_LENGTH`), and a password that breaks it comes back as
 * the server's own sentence, which names it. A protected link also offers to
 * clear the password, which disables the field so the two cannot conflict.
 *
 * Opening the form moves the focus to its first field (F-UIX-007).
 */

import { useEffect, useId, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import { expiryChoiceOf } from "../model/expiryDays";
import type { ExpiryChoice } from "../model/expiryDays";
import { ExpiryOptions } from "./ExpiryOptions";
import styles from "./ShareDialog.module.css";

export interface LinkEditDraft {
  /** The chosen expiry; `"keep"` leaves it as it is. */
  readonly expiry: ExpiryChoice | "keep";
  /** As typed: blank leaves the password as it is. */
  readonly password: string;
  readonly clearPassword: boolean;
}

/** A fresh draft for one link: its expiry left as it is ("Sin cambios"). */
export function freshEdit(): LinkEditDraft {
  return { expiry: "keep", password: "", clearPassword: false };
}

export interface LinkEditorProps {
  /** The name the link is read by (its label, or "no label"). */
  readonly name: string;
  readonly hasPassword: boolean;
  readonly draft: LinkEditDraft;
  readonly onChange: (patch: Partial<LinkEditDraft>) => void;
  readonly refusal?: string | undefined;
  readonly pending: boolean;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

export function LinkEditor({
  name,
  hasPassword,
  draft,
  onChange,
  refusal,
  pending,
  onSubmit,
  onCancel,
}: LinkEditorProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  const firstField = useRef<HTMLSelectElement>(null);
  const expiryHint = t("sharing.expiry.neverHelp");
  const passwordHint = t("sharing.edit.passwordHint");

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  return (
    <form
      className={styles.dialog}
      aria-label={t("sharing.edit.title", { name })}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Field id={`${ids}-expiry`} label={t("sharing.edit.expiryChoice")} hint={expiryHint}>
        <select
          id={`${ids}-expiry`}
          ref={firstField}
          className={styles.select}
          value={String(draft.expiry)}
          aria-describedby={describedBy(`${ids}-expiry`, { hint: expiryHint })}
          disabled={pending}
          onChange={(event) => {
            onChange({ expiry: expiryChoiceOf(event.target.value) ?? "keep" });
          }}
        >
          <option value="keep">{t("sharing.expiry.keep")}</option>
          <ExpiryOptions />
        </select>
      </Field>
      <Field id={`${ids}-password`} label={t("sharing.password.label")} hint={passwordHint}>
        <Input
          id={`${ids}-password`}
          type="password"
          autoComplete="new-password"
          value={draft.password}
          describedBy={describedBy(`${ids}-password`, { hint: passwordHint })}
          disabled={pending || draft.clearPassword}
          onChange={(password) => {
            onChange({ password });
          }}
        />
      </Field>
      {hasPassword ? (
        <label>
          <input
            type="checkbox"
            checked={draft.clearPassword}
            disabled={pending}
            onChange={(event) => {
              onChange({ clearPassword: event.target.checked, password: "" });
            }}
          />{" "}
          {t("sharing.password.clear")}
        </label>
      ) : null}
      {refusal === undefined ? null : (
        <p role="alert" className={styles.refusal}>
          {refusal}
        </p>
      )}
      <div className={styles.actions}>
        <Button variant="primary" type="submit" disabled={pending} loading={pending}>
          {t("sharing.edit.save")}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t("common.action.cancel")}
        </Button>
      </div>
    </form>
  );
}
